/**
 * Static site content — small, rarely changing, safe to bundle everywhere.
 * Marketing copy, taxonomies and the image pool used by page heroes.
 */
import type {
  LicenceTier,
  Office,
  ProcessStep,
  Stat,
  TeamMember,
  TimelineEntry,
  ValueItem,
} from "../types";

export const licenceTiers: LicenceTier[] = [
  {
    "id": "personal",
    "name": "Personal",
    "mult": 1,
    "blurb": "One build, for your own use.",
    "includes": [
      "Single build on one plot",
      "PDF + DWG drawing set",
      "Personal use only",
      "6 months of updates"
    ],
    "excludes": [
      "Resale or redistribution",
      "Use in client work"
    ]
  },
  {
    "id": "commercial",
    "name": "Commercial",
    "mult": 2.4,
    "blurb": "For one client project.",
    "popular": true,
    "includes": [
      "Single client project",
      "All formats incl. DXF & RVT",
      "Sell or lease the built asset",
      "12 months of updates",
      "Stamped title block"
    ],
    "excludes": [
      "Redistribution as a plan set"
    ]
  },
  {
    "id": "extended",
    "name": "Extended",
    "mult": 5,
    "blurb": "Unlimited builds, full modification rights.",
    "includes": [
      "Unlimited builds",
      "All formats + editable source",
      "Modify and rebrand",
      "Lifetime updates",
      "Priority support"
    ],
    "excludes": [
      "Reselling the plan set itself"
    ]
  }
];

export const team: TeamMember[] = [
  {
    "name": "Ar. Rohan Mehta",
    "role": "Principal Architect & Founder",
    "img": "https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&w=800&q=80"
  },
  {
    "name": "Er. Kavita Iyer",
    "role": "Director, Engineering",
    "img": "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=800&q=80"
  },
  {
    "name": "Ar. Ananya Sharma",
    "role": "Design Director",
    "img": "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?auto=format&fit=crop&w=800&q=80"
  },
  {
    "name": "Er. Vikram Nair",
    "role": "Head of Construction",
    "img": "https://images.unsplash.com/photo-1580489944761-15a19d654956?auto=format&fit=crop&w=800&q=80"
  },
  {
    "name": "Meera Krishnan",
    "role": "Head of Development",
    "img": "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=800&q=80"
  },
  {
    "name": "Er. Priya Deshmukh",
    "role": "Senior Structural Engineer",
    "img": "https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&w=800&q=80"
  },
  {
    "name": "Ar. Sameer Qureshi",
    "role": "Associate, Restoration",
    "img": "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&w=800&q=80"
  },
  {
    "name": "Nandita Rao",
    "role": "Head of Client Relations",
    "img": "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&w=800&q=80"
  }
];

export const timeline: TimelineEntry[] = [
  {
    "year": "2011",
    "title": "Practice founded",
    "desc": "Started as a two-person structural consultancy in a Gurugram flat, taking on peer-review work for larger firms."
  },
  {
    "year": "2014",
    "title": "Architecture studio added",
    "desc": "Brought design in-house after too many projects arrived with drawings that could not be built as issued."
  },
  {
    "year": "2017",
    "title": "First turn-key delivery",
    "desc": "The Terraces at Kalindi began as a design commission and became our first full design-and-build contract."
  },
  {
    "year": "2020",
    "title": "Construction arm established",
    "desc": "Formed our own site management and execution team rather than continuing to subcontract delivery."
  },
  {
    "year": "2023",
    "title": "Restoration practice",
    "desc": "Opened a dedicated conservation and retrofit unit following the Chandni Haveli commission."
  },
  {
    "year": "2026",
    "title": "Four cities, one hundred and ten people",
    "desc": "Studios in Gurugram, Jaipur and Dehradun, with live projects across eight cities in North India."
  }
];

export const values: ValueItem[] = [
  {
    "title": "Say the difficult thing early",
    "desc": "A budget problem raised in week three is a conversation. The same problem raised in month eight is a crisis. We front-load the bad news."
  },
  {
    "title": "Design and engineering share a room",
    "desc": "Our engineers sit in concept reviews and our architects attend reinforcement inspections. The handover between them is the weakest point in most projects, so we removed it."
  },
  {
    "title": "Build what we drew",
    "desc": "Specification substitution is the quiet way projects lose their quality. We procure against named products, and we document what went in."
  },
  {
    "title": "The site is the real drawing",
    "desc": "Every senior person in this practice is on site regularly. A detail that cannot be built by the people we actually employ is not a finished detail."
  }
];

export const offices: Office[] = [
  {
    "id": "gurugram",
    "city": "Gurugram",
    "label": "Head Office",
    "address": "4th Floor, Sector 44 Business Centre, Gurugram, Haryana 122003",
    "phone": "+91 124 456 7890",
    "email": "hello@indraprastha.design",
    "hours": [
      {
        "day": "Monday – Friday",
        "time": "9:30 am – 6:30 pm"
      },
      {
        "day": "Saturday",
        "time": "10:00 am – 3:00 pm"
      },
      {
        "day": "Sunday",
        "time": "Closed",
        "closed": true
      }
    ]
  },
  {
    "id": "jaipur",
    "city": "Jaipur",
    "label": "Studio",
    "address": "2nd Floor, Ashok Marg, C-Scheme, Jaipur, Rajasthan 302001",
    "phone": "+91 141 234 5678",
    "email": "jaipur@indraprastha.design",
    "hours": [
      {
        "day": "Monday – Friday",
        "time": "9:30 am – 6:30 pm"
      },
      {
        "day": "Saturday",
        "time": "10:00 am – 2:00 pm"
      },
      {
        "day": "Sunday",
        "time": "Closed",
        "closed": true
      }
    ]
  },
  {
    "id": "dehradun",
    "city": "Dehradun",
    "label": "Studio",
    "address": "Rajpur Road, Near Kalindi Estates, Dehradun, Uttarakhand 248001",
    "phone": "+91 135 678 9012",
    "email": "dehradun@indraprastha.design",
    "hours": [
      {
        "day": "Monday – Friday",
        "time": "10:00 am – 6:00 pm"
      },
      {
        "day": "Saturday",
        "time": "By appointment"
      },
      {
        "day": "Sunday",
        "time": "Closed",
        "closed": true
      }
    ]
  }
];

export const processSteps: ProcessStep[] = [
  {
    "title": "Consultation",
    "desc": "We listen to the brief, walk the site and tell you honestly whether the project as described is achievable within the budget as described."
  },
  {
    "title": "Planning",
    "desc": "Feasibility, statutory envelope, yield and a development budget you can take to a lender or a board."
  },
  {
    "title": "Design",
    "desc": "Concept options with indicative costs, then full design development on the option you select."
  },
  {
    "title": "Engineering",
    "desc": "Structure, services and geotechnics resolved alongside the design, not after it."
  },
  {
    "title": "Execution",
    "desc": "Construction under our own site management, with checklist-gated quality and weekly reporting."
  },
  {
    "title": "Handover",
    "desc": "Testing, commissioning, documentation, warranties — and a defect liability period we actually answer."
  }
];

export const stats: Stat[] = [
  {
    "value": 200,
    "suffix": "+",
    "label": "Projects Completed"
  },
  {
    "value": 25,
    "suffix": "+",
    "label": "Years of Experience"
  },
  {
    "value": 100,
    "suffix": "+",
    "label": "Happy Clients"
  },
  {
    "value": 110,
    "suffix": "+",
    "label": "Expert Team"
  }
];

export const flooringTypes = [
  "Vitrified Tile",
  "Italian Marble",
  "Engineered Wood",
  "Kota Stone",
  "Terrazzo",
  "Anti-skid Ceramic",
  "Epoxy Screed",
  "Polished Concrete"
] as const;

export const infraTypes = [
  "Independent Villa",
  "Apartment Unit",
  "Duplex",
  "Row House",
  "Farmhouse",
  "Office Floor",
  "Retail Unit",
  "Warehouse",
  "Clinic",
  "School Block",
  "Mixed-Use Podium"
] as const;

export const blogCategories = [
  "Architecture",
  "Engineering",
  "Construction",
  "Real Estate",
  "Design",
  "Sustainability",
  "Company News",
  "Industry Insights"
] as const;

export const projectTypes = [
  "Residential",
  "Commercial",
  "Institutional",
  "Industrial",
  "Renovation"
] as const;

export const imagePool = {
  "heroA": "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?auto=format&fit=crop&w=1600&q=80",
  "heroB": "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&w=1600&q=80",
  "heroC": "https://images.unsplash.com/photo-1512917774080-9991f1c4c750?auto=format&fit=crop&w=1600&q=80",
  "villa1": "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1200&q=80",
  "villa2": "https://images.unsplash.com/photo-1600566753086-00f18fb6b3ea?auto=format&fit=crop&w=1200&q=80",
  "villa3": "https://images.unsplash.com/photo-1600047509807-ba8f99d2cdde?auto=format&fit=crop&w=1200&q=80",
  "villa4": "https://images.unsplash.com/photo-1613490493576-7fde63acd811?auto=format&fit=crop&w=1200&q=80",
  "house1": "https://images.unsplash.com/photo-1568605114967-8130f3a36994?auto=format&fit=crop&w=1200&q=80",
  "house2": "https://images.unsplash.com/photo-1570129477492-45c003edd2be?auto=format&fit=crop&w=1200&q=80",
  "house3": "https://images.unsplash.com/photo-1580587771525-78b9dba3b914?auto=format&fit=crop&w=1200&q=80",
  "house4": "https://images.unsplash.com/photo-1564013799919-ab600027ffc6?auto=format&fit=crop&w=1200&q=80",
  "house5": "https://images.unsplash.com/photo-1583608205776-bfd35f0d9f83?auto=format&fit=crop&w=1200&q=80",
  "tower1": "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=1200&q=80",
  "tower2": "https://images.unsplash.com/photo-1497366754035-f200968a6e72?auto=format&fit=crop&w=1200&q=80",
  "tower3": "https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?auto=format&fit=crop&w=1200&q=80",
  "tower4": "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?auto=format&fit=crop&w=1200&q=80",
  "office1": "https://images.unsplash.com/photo-1497366811353-6870744d04b2?auto=format&fit=crop&w=1200&q=80",
  "office2": "https://images.unsplash.com/photo-1524758631624-e2822e304c36?auto=format&fit=crop&w=1200&q=80",
  "facade1": "https://images.unsplash.com/photo-1487958449943-2429e8be8625?auto=format&fit=crop&w=1200&q=80",
  "facade2": "https://images.unsplash.com/photo-1449844908441-8829872d2607?auto=format&fit=crop&w=1200&q=80",
  "facade3": "https://images.unsplash.com/photo-1460317442991-0ec209397118?auto=format&fit=crop&w=1200&q=80",
  "facade4": "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?auto=format&fit=crop&w=1200&q=80",
  "interior1": "https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?auto=format&fit=crop&w=1200&q=80",
  "interior2": "https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?auto=format&fit=crop&w=1200&q=80",
  "interior3": "https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?auto=format&fit=crop&w=1200&q=80",
  "interior4": "https://images.unsplash.com/photo-1600585154526-990dced4db0d?auto=format&fit=crop&w=1200&q=80",
  "site1": "https://images.unsplash.com/photo-1504307651254-35680f356dfd?auto=format&fit=crop&w=1200&q=80",
  "site2": "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?auto=format&fit=crop&w=1200&q=80",
  "site3": "https://images.unsplash.com/photo-1590674899484-d5640e854abe?auto=format&fit=crop&w=1200&q=80",
  "blueprint1": "https://images.unsplash.com/photo-1503387762-592deb58ef4e?auto=format&fit=crop&w=1200&q=80",
  "blueprint2": "https://images.unsplash.com/photo-1487017159836-4e23ece2e4cf?auto=format&fit=crop&w=1200&q=80",
  "team1": "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?auto=format&fit=crop&w=1200&q=80",
  "team2": "https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=1200&q=80",
  "team3": "https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=1200&q=80",
  "portrait1": "https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&w=800&q=80",
  "portrait2": "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=800&q=80",
  "portrait3": "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?auto=format&fit=crop&w=800&q=80",
  "portrait4": "https://images.unsplash.com/photo-1580489944761-15a19d654956?auto=format&fit=crop&w=800&q=80",
  "portrait5": "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=800&q=80",
  "portrait6": "https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&w=800&q=80",
  "portrait7": "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&w=800&q=80",
  "portrait8": "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&w=800&q=80",
  "landscape1": "https://images.unsplash.com/photo-1416331108676-a22ccb276e35?auto=format&fit=crop&w=1200&q=80",
  "landscape2": "https://images.unsplash.com/photo-1600607688969-a5bfcd646154?auto=format&fit=crop&w=1200&q=80",
  "green1": "https://images.unsplash.com/photo-1518005020951-eccb494ad742?auto=format&fit=crop&w=1200&q=80",
  "green2": "https://images.unsplash.com/photo-1493809842364-78817add7ffb?auto=format&fit=crop&w=1200&q=80",
  "school1": "https://images.unsplash.com/photo-1562774053-701939374585?auto=format&fit=crop&w=1200&q=80",
  "clinic1": "https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?auto=format&fit=crop&w=1200&q=80",
  "retail1": "https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&w=1200&q=80",
  "industrial1": "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?auto=format&fit=crop&w=1200&q=80",
  "heritage1": "https://images.unsplash.com/photo-1587474260584-136574528ed5?auto=format&fit=crop&w=1200&q=80",
  "heritage2": "https://images.unsplash.com/photo-1524230572899-a752b3835840?auto=format&fit=crop&w=1200&q=80"
};

